import React, { FC, useMemo } from "react";
import StatusDialog, { StatusDialogProps } from "../../Unknown/StatusDialog";
import Typography from "../../Unknown/Typography";
import useTranslations from "./useTranslations";
import Alert from "../../Unknown/Alert";

export type AppointmentCancelDialogProps = {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: () => void | Promise<void>;
  isLoading?: boolean;
  errorMessage?: string | null;
  isRefundAvailable: boolean;
};

const AppointmentCancelDialog: FC<AppointmentCancelDialogProps> = ({
  isOpen,
  handleClose,
  handleSubmit,
  isLoading,
  errorMessage,
  isRefundAvailable,
}) => {
  const { translations } = useTranslations();

  const actionButtons = useMemo<StatusDialogProps["actionButtons"]>(() => {
    return [
      {
        text: translations.btnCancel,
        key: "cancel",
        onClick: handleClose,
        disabled: isLoading,
      },
      {
        text: translations.btnSubmit,
        key: "submit",
        onClick: handleSubmit,
        color: "error",
        disabled: isLoading || !!errorMessage,
      },
    ];
  }, [
    errorMessage,
    handleClose,
    handleSubmit,
    isLoading,
    translations.btnCancel,
    translations.btnSubmit,
  ]);

  const alertMessage = isRefundAvailable
    ? translations.refundInfoMessage
    : translations.refundWarning;

  const alertSeverity = isRefundAvailable ? "info" : "warning";

  return (
    <StatusDialog
      open={isOpen}
      title={translations.title}
      isLoading={isLoading}
      errorMessage={errorMessage}
      actionButtons={actionButtons}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          maxWidth: 500,
        },
      }}
    >
      <Typography mb={5}>{translations.description}</Typography>
      <Alert severity={alertSeverity}>
        <Typography whiteSpace="pre-wrap" variant="body2">
          {alertMessage}
        </Typography>
      </Alert>
    </StatusDialog>
  );
};

export default AppointmentCancelDialog;
